import {
    Box,
    Card,
    CardContent,
    Chip,
    Link,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {
    Article
} from '@mui/icons-material';
import palette from "../../theme/palette";
import {CellNote, EmptyState, ReportTable, isHttpsUrl, truncate} from "./report-table";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
    handleDownload ?: boolean;
}

// Saved articles carry more than the declared type: the nationwide news search
// adds `category` ("Violent Crime" | "Criminal Activity" | "Legal Proceedings"
// | "General"), `relevanceReasons` (keyword hits such as "Criminal activity:
// arrested") and `snippet`. Older reports have `searchMethod` instead.
type StoredArticle = RiskReportTypes.NewsArticle & {
    category?: string;
    relevanceReasons?: string[];
    snippet?: string;
    relevanceScore?: number;
};

// The search stamps `publishedAt` with the search time when the feed gave no
// real date (113 of 174 DEV articles at the time of writing), so an article
// can't be shown as "published" at the moment the report ran. Anything within
// ten minutes of the search timestamp, or unparseable, is reported as unknown.
export const formatPublishedDate = (publishedAt?: string, searchTimestamp?: string): string | null => {
    if (!publishedAt) return null;
    const published = new Date(publishedAt);
    if (Number.isNaN(published.getTime())) return null;
    const searched = searchTimestamp ? new Date(searchTimestamp) : null;
    if (searched && !Number.isNaN(searched.getTime()) && Math.abs(published.getTime() - searched.getTime()) < 10 * 60 * 1000) {
        return null;
    }
    return published.toLocaleDateString();
};

// "Criminal activity: arrested" -> "arrested". The category is shown as-is;
// "General" means nothing matched, so it is dropped.
export const keywordMatches = (article: StoredArticle): { category: string | null; reasons: string[] } => {
    const category = article.category && article.category !== 'General' ? article.category : null;
    const reasons = Array.from(new Set(
        (article.relevanceReasons ?? [])
            .map((reason) => reason.split(':').pop()?.trim() ?? '')
            .filter(Boolean),
    ));
    return { category, reasons };
};

const hostOf = (url?: string): string => {
    try {
        return new URL(url ?? '').hostname.replace(/^www\./, '');
    } catch {
        // URL() throws on '' and malformed input; no host is the right answer.
        return '';
    }
};

const DASH = '—';

export const NewsArticles: React.FC<Props> = ({
                                                  assessmentData,
                                                  handleDownload
                                              }) => {

    // `findings` is absent on some stored reports. Defaulting the whole object
    // (rather than just guarding the destructure) matters because every use
    // below dereferences `newsArticles.articles` directly — and with no
    // ErrorBoundary in the app, one throw here white-screens the whole page.
    const newsArticles: Partial<RiskReportTypes.NewsArticles> = assessmentData?.riskAssessmentInfo?.findings?.newsArticles ?? {};
    const articles: StoredArticle[] = Array.isArray(newsArticles.articles) ? newsArticles.articles : [];
    const searchTimestamp = newsArticles.timestamp;
    // True in the live page, false in the detached PDF render (links are inert
    // in a rasterised PDF, so they are only drawn live).
    const isLiveView = !!handleDownload;

    return(
        <>

            <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box className={'pdf-keep-with-next'} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: palette.darkBlue, p: 1, borderRadius: 1, mr: 2 }}>
                            <Article sx={{ color: 'white' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" fontWeight={500}>
                                    Related News
                                </Typography>
                                <Chip
                                    label="SUPPLEMENTAL"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '10px', letterSpacing: '0.06em', fontWeight: 600, color: '#637381', borderColor: '#C4CDD5' }}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Automated news search. Not a verified record.
                            </Typography>
                        </Box>
                        {articles.length > 0 && (
                            <Chip
                                label={`${articles.length} ARTICLES`}
                                sx={{
                                    color:'#57A2E9'
                                }}
                                variant="outlined"
                                size="small"
                            />
                        )}

                    </Box>

                    {/* Why this section reads differently from the ones above it.
                        Reviewers were treating articles as findings and were
                        confused when a repeat search a month later returned a
                        different set; the articles stay in the report because
                        the news search is a required part of the assessment. */}
                    <Box className={'pdf-avoid-break'} sx={{ bgcolor: '#F4F6F8', borderRadius: 1, px: 1.75, py: 1.25, mb: 2 }}>
                        <Typography sx={{ fontSize: '12.5px', color: '#454F5B', lineHeight: 1.5 }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>Context, not findings.</Box>{' '}
                            These come from an automated news search on the subject's name and address and are included
                            in every assessment. Results can change over time and may involve someone else with the same
                            name. Confirm an article before acting on it.
                        </Typography>
                    </Box>

                    {articles.length > 0 ? (
                        <ReportTable aria-label="Related news articles">
                            <TableHead className={'pdf-keep-with-next'}>
                                <TableRow>
                                    <TableCell>Published</TableCell>
                                    <TableCell sx={{ width: '55%' }}>Headline</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Keyword Match</TableCell>
                                </TableRow>
                            </TableHead>
                            {articles.map((article, index) => {
                                const published = formatPublishedDate(article.publishedAt, searchTimestamp);
                                const { category, reasons } = keywordMatches(article);
                                const summary = truncate(article.snippet || article.description, 160);
                                const title = article.title || 'Untitled article';
                                return (
                                    <TableBody key={index} className={'pdf-avoid-break'}>
                                        <TableRow>
                                            <TableCell sx={{ whiteSpace: 'nowrap', color: published ? undefined : '#919EAB' }}>
                                                {published ?? 'Unknown'}
                                            </TableCell>
                                            <TableCell>
                                                {isLiveView && isHttpsUrl(article.url) ? (
                                                    <Link
                                                        href={article.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ color: palette.darkBlue, fontWeight: 500 }}
                                                    >
                                                        {title}
                                                    </Link>
                                                ) : (
                                                    <Box component="span" sx={{ fontWeight: 500 }}>{title}</Box>
                                                )}
                                                {summary && <CellNote>{summary}</CellNote>}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {article.source || hostOf(article.url) || DASH}
                                            </TableCell>
                                            <TableCell>
                                                {category || reasons.length > 0 ? (
                                                    <>
                                                        {category && <Box component="div">{category}</Box>}
                                                        {reasons.length > 0 && <CellNote>{reasons.join(', ')}</CellNote>}
                                                    </>
                                                ) : DASH}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                );
                            })}
                        </ReportTable>
                    ):(
                        <EmptyState>No News Articles Found</EmptyState>
                    )}
                </CardContent>
            </Card>

        </>
    )
}
